import React, { useState, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClientsContext } from "@/App";
import { Plus } from "lucide-react";

const UNITS = ["pcs", "kgs", "mtrs", "ltrs"];
const PRIORITIES = ["Standard", "Expedite"];

export default function AddItemModal({ onClose, onCreate }) {
  const { clients } = useContext(ClientsContext);
  const defaultClient = clients[0]?.name || "BH Wireline";

  const [form, setForm] = useState({
    item_description: "",
    po_number: "",
    part_number: "",
    line_number: "001",
    revision: "",
    client: defaultClient,
    quantity: "",
    unit: "pcs",
    vendor: "",
    value_usd: "",
    po_date: new Date().toISOString().split("T")[0],
    due_date: "",
    dispatch_due_date: "",
    priority: "Standard",
    stage: "PO Received",
    stage_comment: "",
    is_qual: false,
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate({
      ...form,
      quantity: parseFloat(form.quantity) || 0,
      value_usd: parseFloat(form.value_usd) || 0,
    });
  };

  const isValid = form.item_description && form.po_number && form.quantity && form.value_usd && form.due_date;

  // Get color for the selected client
  const selectedClientColor = clients.find(c => c.name === form.client)?.color || "#94a3b8";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-xl" data-testid="add-item-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-400" /> New Line Item
            </DialogTitle>
            <DialogDescription>Add a new item to the PO Received stage.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Item Description *</label>
                <input
                  value={form.item_description}
                  onChange={(e) => handleChange("item_description", e.target.value)}
                  className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-item-description"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">PO Number *</label>
                <input
                  value={form.po_number}
                  onChange={(e) => handleChange("po_number", e.target.value)}
                  placeholder="PO-2026-XXX"
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-po-number"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Part Number</label>
                <input
                  value={form.part_number}
                  onChange={(e) => handleChange("part_number", e.target.value)}
                  placeholder="RP-XX-26-001"
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-part-number"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Line Number</label>
                <input
                  value={form.line_number}
                  onChange={(e) => handleChange("line_number", e.target.value)}
                  placeholder="001"
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-line-number"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Revision</label>
                <input
                  value={form.revision}
                  onChange={(e) => handleChange("revision", e.target.value)}
                  placeholder="Rev A"
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-revision"
                />
              </div>

              {/* Client dropdown — dynamic from context */}
              <div className="col-span-2">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Client</label>
                <div className="flex items-center gap-2">
                  {/* Color preview dot */}
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: selectedClientColor }} />
                  <Select value={form.client} onValueChange={(v) => handleChange("client", v)}>
                    <SelectTrigger className="h-9 flex-1 bg-[hsl(var(--muted))] border-[hsl(var(--border))]" data-testid="select-client">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.id || c.name} value={c.name}>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                            {c.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Quantity *</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={form.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="input-quantity"
                  />
                  <Select value={form.unit} onValueChange={(v) => handleChange("unit", v)}>
                    <SelectTrigger className="h-9 w-24 bg-[hsl(var(--muted))] border-[hsl(var(--border))]" data-testid="select-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Value (USD) *</label>
                <input
                  type="number"
                  value={form.value_usd}
                  onChange={(e) => handleChange("value_usd", e.target.value)}
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-value-usd"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Vendor</label>
                <input
                  value={form.vendor}
                  onChange={(e) => handleChange("vendor", e.target.value)}
                  className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-vendor"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(v) => handleChange("priority", v)}>
                  <SelectTrigger className="h-9 bg-[hsl(var(--muted))] border-[hsl(var(--border))]" data-testid="select-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">PO Date</label>
                <input
                  type="date"
                  value={form.po_date}
                  onChange={(e) => handleChange("po_date", e.target.value)}
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-po-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Due Date *</label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => handleChange("due_date", e.target.value)}
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-due-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Dispatch Due Date</label>
                <input
                  type="date"
                  value={form.dispatch_due_date}
                  onChange={(e) => handleChange("dispatch_due_date", e.target.value)}
                  className="w-full h-9 px-3 text-sm font-mono-data rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-dispatch-due-date"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-1 block">Comment</label>
                <input
                  value={form.stage_comment}
                  onChange={(e) => handleChange("stage_comment", e.target.value)}
                  placeholder="Optional note..."
                  className="w-full h-9 px-3 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="input-stage-comment"
                />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-3 cursor-pointer" data-testid="input-is-qual">
                  <input
                    type="checkbox"
                    checked={form.is_qual}
                    onChange={(e) => handleChange("is_qual", e.target.checked)}
                    className="w-4 h-4 rounded border-[hsl(var(--border))] bg-[hsl(var(--muted))] accent-orange-500"
                  />
                  <span className="text-sm text-[hsl(var(--foreground))]">Qualification Order (QUAL)</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Not a production order</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors" data-testid="add-item-cancel">Cancel</button>
              <button type="submit" disabled={!isValid} className="px-4 py-2 rounded-md text-sm font-medium bg-orange-600 text-white hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="add-item-submit">Create Item</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
  );
}
